import { useEffect, useRef, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SharedNavbar from '@/components/feature/SharedNavbar';
import SharedFooter from '@/components/feature/SharedFooter';
import { useLanguageSwitch } from '@/hooks/useLanguage';
import { useReveal } from '@/pages/content/v2/shared';
import '../../home/v2/home-v2.css';
import '../../content/v2/content-v2.css';
import './info-v2.css';

// Shared frame for the new-design support pages (About, Contact, FAQ,
// Shipping & Returns, Privacy, Terms): the journal masthead used on the
// Content hub, a row of chips linking the support pages, the page body and
// a closing "still need help" band. All text comes from the same i18n keys
// as the classic pages, so Admin > Website edits apply to both designs.

export const SUPPORT_PAGES = [
  { to: '/about', key: 'footer.aboutUs', def: 'About Us' },
  { to: '/contact', key: 'footer.contactUs', def: 'Contact Us' },
  { to: '/faq', key: 'footer.faq', def: 'FAQ' },
  { to: '/shipping-returns', key: 'footer.shippingReturns', def: 'Shipping & Returns' },
  { to: '/privacy-policy', key: 'footer.privacyPolicy', def: 'Privacy Policy' },
  { to: '/terms-of-service', key: 'footer.termsOfService', def: 'Terms of Service' },
];

export function usePrefix() {
  const { currentLang } = useLanguageSwitch();
  return currentLang === 'ko' ? '/ko' : currentLang === 'bn' ? '/bn' : '';
}

export const Icon = ({ d, className }: { d: string | string[]; className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden>
    {(Array.isArray(d) ? d : [d]).map((p, i) => (
      <path key={i} d={p} />
    ))}
  </svg>
);

export const ICONS = {
  arrow: 'M5 12h14M13 6l6 6-6 6',
  mail: ['M4 6h16v12H4z', 'M4 7l8 6 8-6'],
  phone: 'M6.5 3.5l3 .5 1.2 4-2 1.5a12 12 0 0 0 5.8 5.8l1.5-2 4 1.2.5 3a2 2 0 0 1-2 2A17 17 0 0 1 4.5 5.5a2 2 0 0 1 2-2z',
  chat: ['M4 5h16v11H9l-5 4z', 'M8 10h8M8 13h5'],
  building: ['M5 21V5l7-2v18', 'M12 8h7v13', 'M8 8h1M8 12h1M8 16h1M15 12h1M15 16h1', 'M3 21h18'],
  pin: ['M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z', 'M12 7.5a2.2 2.2 0 1 0 0 4.4 2.2 2.2 0 0 0 0-4.4z'],
  truck: ['M3 6h11v10H3z', 'M14 10h4l3 3v3h-7', 'M7 18.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zM17 18.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z'],
  gift: ['M4 11h16v9H4z', 'M3 7h18v4H3z', 'M12 7v13', 'M12 7c-2-3-5-3-5-1s3 1 5 1c2 0 5 1 5-1s-3-2-5 1'],
  check: 'M5 12.5l4.5 4.5L19 7.5',
  close: 'M6 6l12 12M18 6L6 18',
  search: ['M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14z', 'M20 20l-4-4'],
  plus: 'M12 5v14M5 12h14',
  shield: ['M12 3l8 3v6c0 4.5-3.4 8.3-8 9-4.6-.7-8-4.5-8-9V6z', 'M8.5 12l2.5 2.5 4.5-5'],
  flask: ['M9 3h6', 'M10 3v6L4.5 19A1.5 1.5 0 0 0 5.8 21h12.4a1.5 1.5 0 0 0 1.3-2L14 9V3', 'M7 15h10'],
  qr: ['M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4z', 'M14 14h2v2h-2zM18 14h2v2h-2zM14 18h2v2h-2zM18 18h2v2h-2z'],
  doc: ['M6 3h9l4 4v14H6z', 'M14 3v5h5', 'M9 12h7M9 16h7'],
  return: ['M9 14L4 9l5-5', 'M4 9h10a6 6 0 0 1 0 12h-3'],
  clock: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z', 'M12 7v5l3 2'],
};

export function InfoShell({
  label,
  title,
  lede,
  meta,
  children,
  help = true,
  nav = true,
}: {
  label: string;
  title: string;
  lede?: ReactNode;
  meta?: ReactNode;
  children: ReactNode;
  help?: boolean;
  nav?: boolean;
}) {
  const { t } = useTranslation();
  const prefix = usePrefix();
  const { pathname } = useLocation();
  const root = useReveal<HTMLDivElement>();
  const chips = useRef<HTMLDivElement>(null);
  const here = pathname.replace(/^\/(ko|bn)(?=\/)/, '');
  const wa = t('contact.channels.whatsapp.value').replace(/[^\d]/g, '');
  const email = t('contact.channels.email.value');

  // On phones the chip row scrolls sideways: bring this page's chip into view.
  useEffect(() => {
    const row = chips.current;
    const on = row?.querySelector<HTMLElement>('a.on');
    if (row && on) row.scrollLeft = on.offsetLeft - (row.clientWidth - on.offsetWidth) / 2;
  }, [here]);

  return (
    <>
      <SharedNavbar />
      <main className="w-full min-h-screen bg-white">
        <div ref={root} className="v2 cnt inf" style={{ paddingTop: 'var(--v2h-h, 64px)' }}>
          <header className="chead">
            <div className="wrap">
              <div className="chl">
                <span className="lbl rv">{label}</span>
                <h1 className="rv" style={{ ['--d' as string]: '.06s' }}>
                  {title}
                </h1>
              </div>
              <div className="chr rv" style={{ ['--d' as string]: '.14s' }}>
                {lede && <p>{lede}</p>}
                {meta}
              </div>
            </div>
          </header>

          {nav && (
            <nav className="ctabs inav" aria-label={t('footer.support', 'Support')}>
              <div className="wrap">
                <div className="chips" ref={chips}>
                  {SUPPORT_PAGES.map((p) => (
                    <Link key={p.to} to={prefix + p.to} className={here === p.to ? 'on' : ''} aria-current={here === p.to ? 'page' : undefined}>
                      {t(p.key, p.def)}
                    </Link>
                  ))}
                </div>
              </div>
            </nav>
          )}

          {children}

          {help && (
            <section className="ihelp">
              <div className="wrap rv">
                <div className="ihl">
                  <span className="lbl">{t('infoV2.help.label', 'We are here for you')}</span>
                  <h2>{t('faq.bottom.title', 'Still have questions?')}</h2>
                  <p>{t('faq.bottom.body', 'Our support team is ready to help. Reach out any time.')}</p>
                </div>
                <div className="ihr">
                  {wa && (
                    <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" className="ich">
                      <Icon d={ICONS.chat} />
                      <span>
                        <small>WhatsApp</small>
                        <b>{t('contact.channels.whatsapp.value')}</b>
                      </span>
                    </a>
                  )}
                  {email.includes('@') && (
                    <a href={`mailto:${email}`} className="ich">
                      <Icon d={ICONS.mail} />
                      <span>
                        <small>{t('contact.channels.email.label', 'Email')}</small>
                        <b>{email}</b>
                      </span>
                    </a>
                  )}
                  <Link to={prefix + '/contact'} className="btn dark">
                    {t('infoV2.help.cta', 'Contact us')}
                    <Icon d={ICONS.arrow} />
                  </Link>
                </div>
              </div>
            </section>
          )}
        </div>
        <SharedFooter />
      </main>
    </>
  );
}
